import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  getAdminAnalyticsOverview,
  type AdminAnalyticsBehaviorRow,
  type AdminAnalyticsOverview,
  type AdminAnalyticsSignupRow,
  type AdminAnalyticsSourceRow,
  type AdminAnalyticsVisitorRow,
} from '../../api/adminAnalyticsApi';

const BORDER = '1px solid #e2e8f0';
const TXT = '#172033';
const SUB = '#596678';
const SUB2 = '#8793a2';
const BLUE = '#0b4f71';
const GOOD = '#2f7d4a';
const RED = '#c94747';
const AMBER = '#a06a0a';
const CARD = '#f8fafc';

const PERIODS = [7, 30, 90] as const;
type PeriodDays = (typeof PERIODS)[number];

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
}

function pct(value: unknown): string {
  const n = nullableNum(value);
  return n === null ? '-' : `${n.toFixed(1)}%`;
}

function fmtInt(value: unknown): string {
  return Math.round(num(value)).toLocaleString('ko-KR');
}

function fmtSeconds(value: unknown): string {
  const seconds = nullableNum(value);
  if (seconds === null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}초`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}분 ${rest}초`;
}

function fmtVisitorEngagement(row: AdminAnalyticsVisitorRow): string {
  const status = String(row.engagementStatus ?? '').trim().toUpperCase();
  const seconds = nullableNum(row.visibleSeconds);

  if (status === 'UNKNOWN' || seconds === null) return '측정 불가';
  const formatted = fmtSeconds(seconds);
  return status === 'LOWER_BOUND' || flag(row.visibleLowerBound)
    ? `≥ ${formatted}`
    : formatted;
}

function fmtDateTime(value: unknown): string {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDate(value: unknown): string {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
}

function behaviorLabel(code: unknown): string {
  const key = String(code ?? '').trim();
  const labels: Record<string, string> = {
    landing_view: '신규/세션 랜딩',
    return_d1: 'D1 재방문',
    return_d3: 'D3 재방문',
    return_d7: 'D7 재방문',
    main_view: '메인 진입',
    main_stay_5s: '메인 5초 체류',
    main_stay_15s: '메인 15초 체류',
    main_stay_30s: '메인 30초 체류',
    main_first_click: '메인 첫 클릭',
    main_action_desk_open_click: 'Action Desk 열기',
    live_summary_locked_click: '로그인 필요 기능 클릭',
    auth_success: '로그인 완료',
    signup_complete: '가입 완료',
    live_summary_authenticated_view: 'Live Summary 로그인 사용',
    live_summary_stock_detail_open: '종목 상세 열기',
    stock_detail_view: '종목 상세 조회',
    watch_stock_add: '종목 추적 등록',
    portfolio_stock_add: '포트폴리오 종목 등록',
    live_summary_watchlist_add_success: '관심종목 추가 완료',
    judgement_view: '판단 조회',
    why_view: '왜 조회',
    validation_view: '검증 조회',
    stock_brief_change_open: 'Change 브리프 열기',
    post_login_panel_continue_click: '로그인 후 분석 계속',
  };
  return labels[key] ?? key.replace(/_/g, ' ');
}

function sourceLabel(source: unknown): string {
  const value = String(source ?? '').trim();
  const labels: Record<string, string> = {
    DIRECT: '직접 유입',
    NAVER: '네이버',
    GOOGLE: '구글',
    DAUM_KAKAO: '다음/카카오',
    BING: '빙',
    OTHER_REFERRAL: '기타 외부',
  };
  return labels[value] ?? (value || '확인 안 됨');
}

function KpiCard({ label, value, sub, tone = 'normal' }: {
  label: string;
  value: string;
  sub: string;
  tone?: 'normal' | 'good' | 'warn';
}) {
  const accent = tone === 'good' ? GOOD : tone === 'warn' ? AMBER : BLUE;
  return (
    <Paper elevation={0} sx={{ p: 1.15, borderRadius: 2.2, border: BORDER, bgcolor: '#fff', minWidth: 0 }}>
      <Typography sx={{ color: SUB2, fontSize: 10.2, fontWeight: 900 }}>{label}</Typography>
      <Typography sx={{ mt: 0.25, color: accent, fontSize: 20, fontWeight: 950, letterSpacing: '-.025em' }}>{value}</Typography>
      <Typography sx={{ mt: 0.2, color: SUB, fontSize: 10.2, lineHeight: 1.45 }}>{sub}</Typography>
    </Paper>
  );
}

function BehaviorTags({ row }: { row: AdminAnalyticsSignupRow | AdminAnalyticsVisitorRow }) {
  const tags: string[] = [];
  if (flag((row as AdminAnalyticsSignupRow).liveSummaryUsed)) tags.push('Live Summary');
  if (flag(row.actionDesk)) tags.push('Action Desk');
  if (flag(row.stockDetail)) tags.push('종목 상세');
  if (flag(row.watchlistAdded)) tags.push('종목 등록');
  if (flag((row as AdminAnalyticsVisitorRow).protectedInterest)) tags.push('로그인 관심');
  if (flag((row as AdminAnalyticsVisitorRow).loggedIn)) tags.push('로그인');
  if (!tags.length) tags.push('탐색 단계');

  return (
    <Box sx={{ display: 'flex', gap: 0.35, flexWrap: 'wrap' }}>
      {tags.slice(0, 4).map((tag) => (
        <Chip key={tag} size="small" label={tag} sx={{ height: 20, fontSize: 9.5, fontWeight: 850, bgcolor: '#eef3f7', color: '#425466' }} />
      ))}
    </Box>
  );
}

function RetentionValue({ returned, eligible, rate }: { returned: unknown; eligible: unknown; rate: unknown }) {
  return (
    <>
      <Typography sx={{ color: TXT, fontSize: 11.4, fontWeight: 950 }}>{pct(rate)}</Typography>
      <Typography sx={{ color: SUB2, fontSize: 9.1 }}>{fmtInt(returned)}/{fmtInt(eligible)}명</Typography>
    </>
  );
}

export default function AdminAnalyticsPanel() {
  const [days, setDays] = useState<PeriodDays>(30);
  const [data, setData] = useState<AdminAnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (period = days) => {
    setLoading(true);
    setError('');
    try {
      setData(await getAdminAnalyticsOverview(period));
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) setError('로그인 세션을 확인해 주세요.');
      else if (status === 403) setError('운영자 권한이 필요합니다.');
      else setError(e?.response?.data?.message || e?.message || '운영 통계를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(days); }, [days, load]);

  const summary = data?.summary ?? {};
  const growth = data?.growth ?? {};
  const daily = useMemo(() => Array.isArray(data?.daily) ? data?.daily ?? [] : [], [data?.daily]);
  const funnel = Array.isArray(data?.funnel) ? data?.funnel ?? [] : [];
  const behaviors = Array.isArray(data?.behaviors) ? data?.behaviors ?? [] : [];
  const sources = Array.isArray(data?.sources) ? data?.sources ?? [] : [];
  const routes = Array.isArray(data?.routes) ? data?.routes ?? [] : [];
  const recentVisitors = Array.isArray(data?.recentVisitors) ? data?.recentVisitors ?? [] : [];
  const recentSignups = Array.isArray(data?.recentSignups) ? data?.recentSignups ?? [] : [];

  const mainUv = num(summary.mainUv);
  const stay15Rate = mainUv > 0 ? (num(summary.stay15Uv) * 100) / mainUv : null;
  const stay30Rate = mainUv > 0 ? (num(summary.stay30Uv) * 100) / mainUv : null;
  const growthRate = nullableNum(growth.growthRatePct);
  const dailyVisible = useMemo(() => daily.slice(-30), [daily]);
  const maxDailyUv = Math.max(1, ...dailyVisible.map((row) => num(row.mainUv)));
  const growthText = growthRate === null
    ? '직전 동일 기간 비교 기준 없음'
    : `${growthRate >= 0 ? '+' : ''}${growthRate.toFixed(1)}% · 직전 동일 기간 대비`;

  return (
    <Paper elevation={0} sx={{ p: { xs: 1, md: 1.2 }, borderRadius: 2.6, border: BORDER, bgcolor: '#fbfcfd' }}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ color: TXT, fontSize: 13.5, fontWeight: 950 }}>사용자 · 획득 · 활성화</Typography>
          <Typography sx={{ mt: 0.2, color: SUB, fontSize: 10.5, lineHeight: 1.5 }}>
            사람(USER) 기준 KPI와 순차 Funnel을 분리합니다. ADMIN 계정·연결 익명키·확인된 내부 디바이스 fingerprint는 서버 집계에서 제외합니다.
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.45} alignItems="center">
          {PERIODS.map((period) => (
            <Button key={period} size="small" variant={days === period ? 'contained' : 'outlined'} onClick={() => setDays(period)}
              sx={{ minWidth: 48, minHeight: 30, px: 0.85, borderRadius: 1.6, fontSize: 10.5, fontWeight: 900, textTransform: 'none' }}>
              {period}일
            </Button>
          ))}
          <Button size="small" variant="text" onClick={() => void load(days)} disabled={loading}
            sx={{ minWidth: 56, minHeight: 30, px: 0.7, fontSize: 10.5, fontWeight: 900, textTransform: 'none' }}>
            {loading ? <CircularProgress size={14} /> : '새로고침'}
          </Button>
        </Stack>
      </Box>

      {error ? (
        <Box sx={{ mt: 1, px: 1, py: 0.8, borderRadius: 1.7, bgcolor: '#fff4f4', border: '1px solid #f0caca' }}>
          <Typography sx={{ color: RED, fontSize: 11, fontWeight: 850 }}>{error}</Typography>
        </Box>
      ) : null}

      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', md: 'repeat(3,minmax(0,1fr))', xl: 'repeat(6,minmax(0,1fr))' }, gap: 0.65 }}>
        <KpiCard label="신규 방문자" value={fmtInt(summary.newVisitorUv)} sub={`landing cohort day 0 · 브라우저/계정 연결 기준`} />
        <KpiCard label={`신규 가입 · ${days}일`} value={fmtInt(growth.newUsersPeriod)} sub={`오늘 +${fmtInt(growth.newUsersToday)} · ${growthText}`} tone={growthRate !== null && growthRate > 0 ? 'good' : 'normal'} />
        <KpiCard label="종목 등록 사용자" value={fmtInt(summary.trackedStockUv)} sub="관심종목·포트폴리오 등록 완료 기준" />
        <KpiCard label="판단 조회 사용자" value={fmtInt(summary.judgementViewUv)} sub="judgement_view 사람 기준" />
        <KpiCard label="첫 Change 경험" value={fmtInt(summary.changeExperienceUv)} sub="Change 탭/변화 브리프 실제 열람 기준" />
        <KpiCard label="24h 활성화" value={fmtInt(growth.newUsersActivatedWithin24h)} sub={`관찰 가능 ${fmtInt(growth.newUsersEligibleFor24h)}명 중 · ${pct(growth.newUserActivationRatePct)}`} tone="good" />
      </Box>

      <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr .9fr' }, gap: 0.75 }}>
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={0.8}>
            <Box>
              <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>메인 방문 · 실제 활성 체류</Typography>
              <Typography sx={{ mt: 0.12, color: SUB2, fontSize: 9.5 }}>heartbeat/visible 시간 authority · wall-clock 탭 방치 시간 제외</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,auto)', gap: 1.2 }}>
              <Box><Typography sx={{ color: SUB2, fontSize: 8.8 }}>평균</Typography><Typography sx={{ color: TXT, fontSize: 11, fontWeight: 950 }}>{fmtSeconds(summary.avgEngagedSeconds)}</Typography></Box>
              <Box><Typography sx={{ color: SUB2, fontSize: 8.8 }}>Median</Typography><Typography sx={{ color: TXT, fontSize: 11, fontWeight: 950 }}>{fmtSeconds(summary.medianEngagedSeconds)}</Typography></Box>
              <Box><Typography sx={{ color: SUB2, fontSize: 8.8 }}>P75</Typography><Typography sx={{ color: TXT, fontSize: 11, fontWeight: 950 }}>{fmtSeconds(summary.p75EngagedSeconds)}</Typography></Box>
            </Box>
          </Stack>
          <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 0.55 }}>
            <Box sx={{ p: 0.7, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>메인 방문자</Typography><Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>{fmtInt(summary.mainUv)}</Typography></Box>
            <Box sx={{ p: 0.7, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>15초 이상</Typography><Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>{fmtInt(summary.stay15Uv)} <small>{stay15Rate === null ? '-' : `${stay15Rate.toFixed(1)}%`}</small></Typography></Box>
            <Box sx={{ p: 0.7, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>30초 이상</Typography><Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>{fmtInt(summary.stay30Uv)} <small>{stay30Rate === null ? '-' : `${stay30Rate.toFixed(1)}%`}</small></Typography></Box>
            <Box sx={{ p: 0.7, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>체류 측정 커버리지</Typography><Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>{pct(summary.engagementCoveragePct)}</Typography></Box>
          </Box>
          <Typography sx={{ mt: 0.6, color: SUB2, fontSize: 9.2 }}>
            진입 당시 상태: 비로그인 {fmtInt(summary.guestMainUv)}명 · 로그인 {fmtInt(summary.memberMainUv)}명 · 양쪽 상태 경험 {fmtInt(summary.guestMemberOverlapUv)}명. 서로 배타적인 회원 분류가 아닙니다.
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>D1 / D3 / D7 재방문</Typography>
          <Typography sx={{ mt: 0.15, color: SUB2, fontSize: 9.5 }}>해당 날짜까지 관찰 가능한 first-landing cohort만 분모에 포함</Typography>
          <Box sx={{ mt: 0.8, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 0.6 }}>
            <Box sx={{ p: 0.75, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>D1</Typography><RetentionValue returned={summary.retentionD1Returned} eligible={summary.retentionD1Eligible} rate={summary.retentionD1RatePct} /></Box>
            <Box sx={{ p: 0.75, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>D3</Typography><RetentionValue returned={summary.retentionD3Returned} eligible={summary.retentionD3Eligible} rate={summary.retentionD3RatePct} /></Box>
            <Box sx={{ p: 0.75, bgcolor: CARD, borderRadius: 1.5 }}><Typography sx={{ color: SUB2, fontSize: 9 }}>D7</Typography><RetentionValue returned={summary.retentionD7Returned} eligible={summary.retentionD7Eligible} rate={summary.retentionD7RatePct} /></Box>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.12fr .88fr' }, gap: 0.75 }}>
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
            <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>일별 인입과 신규 가입</Typography>
            <Typography sx={{ color: SUB2, fontSize: 9.5 }}>{days > 30 ? '최근 30일 표시 · ' : ''}막대=메인 사람 · 점=가입</Typography>
          </Box>
          <Box sx={{ mt: 0.8, display: 'flex', gap: 0.35, alignItems: 'flex-end', height: 116, overflowX: 'auto', pb: 0.25 }}>
            {dailyVisible.map((row) => {
              const main = num(row.mainUv);
              const newcomers = num(row.newUsers);
              const h = Math.max(3, Math.round((main / maxDailyUv) * 84));
              return (
                <Box key={String(row.statDate)} title={`${row.statDate}: 방문 ${main}, 신규 ${newcomers}`} sx={{ minWidth: days === 7 ? 52 : 19, flex: days === 7 ? 1 : '0 0 auto', textAlign: 'center' }}>
                  <Typography sx={{ color: newcomers > 0 ? GOOD : SUB2, fontSize: 8.8, fontWeight: 950, minHeight: 13 }}>{newcomers > 0 ? `+${newcomers}` : ''}</Typography>
                  <Box sx={{ mx: 'auto', width: days === 7 ? 20 : 11, height: h, minHeight: 3, borderRadius: '4px 4px 1px 1px', bgcolor: '#92b7c9' }} />
                  {(days === 7 || dailyVisible.indexOf(row) % 5 === 0 || dailyVisible.indexOf(row) === dailyVisible.length - 1) ? (
                    <Typography sx={{ mt: 0.25, color: SUB2, fontSize: 8.2, whiteSpace: 'nowrap' }}>{fmtDate(row.statDate)}</Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>메인 시작 순차 Funnel</Typography>
          <Typography sx={{ mt: 0.15, color: SUB2, fontSize: 9.6 }}>아래 단계는 앞 단계를 실제 시간순으로 통과한 사용자만 포함합니다. 상단 독립 KPI와 직접 비교하지 않습니다.</Typography>
          <Stack spacing={0.55} sx={{ mt: 0.75 }}>
            {funnel.map((row) => {
              const rate = nullableNum(row.ratePct) ?? 0;
              return (
                <Box key={String(row.code)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: SUB, fontSize: 10.4, fontWeight: 850 }}>{row.label || row.code}</Typography>
                    <Typography sx={{ color: TXT, fontSize: 10.4, fontWeight: 950 }}>{fmtInt(row.users)} · {rate.toFixed(1)}%</Typography>
                  </Box>
                  <Box sx={{ mt: 0.28, height: 5, borderRadius: 4, bgcolor: '#edf1f4', overflow: 'hidden' }}><Box sx={{ width: `${Math.max(0, Math.min(100, rate))}%`, height: '100%', bgcolor: rate >= 50 ? '#6ea286' : '#8aaec0' }} /></Box>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 0.75 }}>
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>많이 한 행동 · 사람 기준</Typography>
          <Stack spacing={0.35} sx={{ mt: 0.65 }}>
            {behaviors.slice(0, 8).map((row: AdminAnalyticsBehaviorRow) => (
              <Box key={String(row.eventType)} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.7, alignItems: 'center' }}>
                <Typography noWrap title={String(row.eventType ?? '')} sx={{ color: SUB, fontSize: 10.3, fontWeight: 800 }}>{behaviorLabel(row.eventType)}</Typography>
                <Typography sx={{ color: TXT, fontSize: 10.3, fontWeight: 950 }}>{fmtInt(row.uv)}명 · {fmtInt(row.eventCount)}회</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>획득 경로</Typography>
          <Typography sx={{ mt: 0.12, color: SUB2, fontSize: 9.2 }}>first touch는 사람 기준 · session은 방문 세션 기준 · 내부 이동 제외</Typography>
          <Stack spacing={0.42} sx={{ mt: 0.65 }}>
            {sources.slice(0, 8).map((row: AdminAnalyticsSourceRow) => (
              <Box key={String(row.source)} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.7, alignItems: 'center' }}>
                <Typography sx={{ color: SUB, fontSize: 10.3, fontWeight: 800 }}>{sourceLabel(row.source)}</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: TXT, fontSize: 10.2, fontWeight: 950 }}>{fmtInt(row.uv)}명 · {pct(row.sharePct)}</Typography>
                  <Typography sx={{ color: SUB2, fontSize: 8.8 }}>session {fmtInt(row.sessionCount)} · {pct(row.sessionSharePct)}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>많이 본 경로</Typography>
          <Stack spacing={0.42} sx={{ mt: 0.65 }}>
            {routes.slice(0, 8).map((row) => (
              <Box key={String(row.path)} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.7, alignItems: 'center' }}>
                <Typography noWrap title={String(row.path ?? '')} sx={{ color: SUB, fontSize: 10.3, fontWeight: 800 }}>{row.path || '/'}</Typography>
                <Typography sx={{ color: TXT, fontSize: 10.3, fontWeight: 950 }}>{fmtInt(row.uv)}명 · {fmtInt(row.views)}뷰</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ mt: 0.75, p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>최근 신규 사용자</Typography>
          <Typography sx={{ color: SUB2, fontSize: 9.5 }}>24h가 지난 가입자만 관찰 가능 분모에 포함</Typography>
        </Box>
        <Divider sx={{ my: 0.65 }} />
        <Stack spacing={0.5}>
          {recentSignups.map((row: AdminAnalyticsSignupRow) => (
            <Box key={String(row.userId)} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(190px,1.1fr) 110px 105px minmax(180px,1fr)' }, gap: { xs: 0.35, md: 0.8 }, alignItems: 'center', p: 0.7, borderRadius: 1.5, bgcolor: CARD }}>
              <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ color: TXT, fontSize: 10.8, fontWeight: 950 }}>{row.email || `user #${row.userId ?? '-'}`}</Typography><Typography sx={{ color: SUB2, fontSize: 9.4 }}>{fmtDateTime(row.createdAt)} 가입</Typography></Box>
              <Chip size="small" label={flag(row.activatedWithin24h) ? '24h 활성' : '미활성/관찰 중'} sx={{ height: 20, fontSize: 9.2, fontWeight: 900, bgcolor: flag(row.activatedWithin24h) ? '#edf8f1' : '#f2f4f7', color: flag(row.activatedWithin24h) ? GOOD : SUB }} />
              <Typography sx={{ color: SUB, fontSize: 9.8 }}>{fmtInt(row.eventCount)} 이벤트<br />최근 {fmtDateTime(row.lastActivityAt)}</Typography>
              <BehaviorTags row={row} />
            </Box>
          ))}
          {!recentSignups.length && <Typography sx={{ color: SUB, fontSize: 10.5 }}>선택 기간에 신규 가입자가 없습니다.</Typography>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ mt: 0.75, p: 1, borderRadius: 2, border: BORDER, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
          <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>최근 사용자 행동 · identity stitched</Typography>
          <Typography sx={{ color: SUB2, fontSize: 9.5 }}>로그인 사용자는 user_id로 묶고, 순수 비회원만 anon_id 기준</Typography>
        </Box>
        <Divider sx={{ my: 0.65 }} />
        <Stack spacing={0.45}>
          {recentVisitors.slice(0, 25).map((row: AdminAnalyticsVisitorRow, index) => (
            <Box key={`${row.visitorKey || 'visitor'}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '145px minmax(170px,.9fr) 90px 95px minmax(180px,1.2fr)' }, gap: { xs: 0.25, md: 0.8 }, alignItems: 'center', p: 0.68, borderRadius: 1.5, border: '1px solid #edf0f3' }}>
              <Box>
                <Typography sx={{ color: TXT, fontSize: 10.5, fontWeight: 950 }}>{row.userType === 'MEMBER' ? '회원' : '비회원'} · {row.visitorKey || '-'}</Typography>
                <Typography sx={{ color: SUB2, fontSize: 9.2 }}>
                  최근 {fmtDateTime(row.lastSeenAt)}
                  {num(row.sourceAnonCount) > 1 ? ` · identity ${fmtInt(row.sourceAnonCount)}키` : ''}
                </Typography>
              </Box>
              <Typography noWrap title={String(row.email || row.lastPath || '')} sx={{ color: SUB, fontSize: 9.8 }}>{row.email || row.lastPath || '-'}</Typography>
              <Typography
                title={String(row.engagementStatus || '')}
                sx={{ color: String(row.engagementStatus || '').toUpperCase() === 'UNKNOWN' ? AMBER : SUB, fontSize: 9.8 }}
              >
                활성 {fmtVisitorEngagement(row)}
              </Typography>
              <Typography sx={{ color: SUB, fontSize: 9.8 }}>이벤트 {fmtInt(row.eventCount)}<br />스크롤 {nullableNum(row.maxScrollPct) === null ? '-' : `${Math.round(num(row.maxScrollPct))}%`}</Typography>
              <BehaviorTags row={row} />
            </Box>
          ))}
        </Stack>
      </Paper>

      <Typography sx={{ mt: 0.7, color: SUB2, fontSize: 9.4, lineHeight: 1.55 }}>
        집계 단위: canonical person(USER 우선, 미로그인 anon fallback). ADMIN user/anon/확인된 내부 device fingerprint는 외부 지표에서 제외합니다. 체류는 visible main_exit를 우선하고, 없으면 heartbeat·5/15/30초 milestone을 하한(≥)으로 사용합니다. lifecycle 증거가 없으면 0초가 아니라 측정 불가입니다.
      </Typography>
    </Paper>
  );
}
